import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router'
import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/uiStore'
import { Dialog, DialogContent } from '@/components/ui/Dialog'
import { ALL_NAVIGATION_ITEMS } from '@/components/layout/navigation'
import { useModules } from '@/features/organizations/hooks/useModules'
import {
  MINIMUM_QUERY_LENGTH,
  SEARCH_KIND_LABELS,
  searchApi,
  searchHitHref,
  searchKeys,
} from '@/features/search/api/searchApi'
import type { SearchHit } from '@/types/search'

/**
 * Global søgning (Cmd/Ctrl + K).
 *
 * To kilder i én liste: navigationen og serverens søgning. Navigationen ligger
 * lokalt, fordi "gå til Projekter" skal virke i samme øjeblik, man taster —
 * ikke efter et netværkskald. Alt indhold kommer fra serveren, som er den
 * eneste, der kan afgøre, hvad brugeren må se.
 *
 * Søgeordet forsinkes, så der ikke sendes et kald pr. tastetryk. Forsinkelsen
 * er kort nok til at føles som direkte respons og lang nok til, at en sætning
 * skrevet i ét stræk kun bliver til én forespørgsel.
 */
const SEARCH_DEBOUNCE_MS = 250

export function CommandPalette() {
  const open = useUiStore((state) => state.commandPaletteOpen)
  const setOpen = useUiStore((state) => state.setCommandPaletteOpen)
  const navigate = useNavigate()
  const { isEnabled } = useModules()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const pages = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    // Flad liste på tværs af grupperne: en menu, der er foldet sammen, må ikke
    // skjule et punkt for søgningen. Et fravalgt modul skal den derimod skjule
    // — paletten er den anden vej ind i menuen og skal vise det samme som den.
    const available = ALL_NAVIGATION_ITEMS.filter(
      (item) => !item.comingSoon && (item.module === undefined || isEnabled(item.module)),
    )

    if (!normalized) return available

    return available.filter((item) => item.label.toLowerCase().includes(normalized))
  }, [query, isEnabled])

  const results = useQuery({
    queryKey: searchKeys.query(debounced),
    queryFn: () => searchApi.search(debounced),
    enabled: open && debounced.length >= MINIMUM_QUERY_LENGTH,
    // Søgeresultater bliver hurtigt gamle, men to tastetryk frem og tilbage
    // skal ikke koste to kald.
    staleTime: 30 * 1000,
  })

  const groups = results.data?.groups ?? []
  const hasContent = groups.length > 0
  const isSearching = debounced.length >= MINIMUM_QUERY_LENGTH && results.isFetching

  function handleSelect(to: string) {
    setOpen(false)
    setQuery('')
    setDebounced('')
    void navigate(to)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setQuery('')
          setDebounced('')
        }
      }}
    >
      <DialogContent
        title="Søg i BygOverblik"
        description="Søg efter projekter, dokumenter, tegninger, opgaver, mangler, udbud, materialer og anlæg."
        size="md"
      >
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted"
              aria-hidden="true"
            />
            <input
              // Autofokus er korrekt her: dialogen åbnes udelukkende for at søge.
              autoFocus
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Skriv for at søge…"
              aria-label="Søgeord"
              className={cn(
                'h-11 w-full rounded-[var(--radius-control)] border border-line-subtle',
                'bg-app-900/60 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted',
                'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
              )}
            />
          </div>

          <div
            className="flex max-h-96 flex-col gap-3 overflow-y-auto"
            // Resultaterne udskiftes, mens man skriver. Uden dette læser en
            // skærmlæser kun det, der stod der, da feltet fik fokus.
            aria-live="polite"
            aria-busy={isSearching}
          >
            {pages.length > 0 && (
              <Section label="Sider">
                {pages.map((item) => {
                  const Icon = item.icon

                  return (
                    <ResultRow
                      key={item.to}
                      onSelect={() => handleSelect(item.to)}
                      title={item.label}
                    >
                      <Icon className="size-4 shrink-0 text-text-muted" aria-hidden="true" />
                    </ResultRow>
                  )
                })}
              </Section>
            )}

            {groups.map((group) => (
              <Section
                key={group.kind}
                label={SEARCH_KIND_LABELS[group.kind]}
                note={group.hasMore ? 'Der er flere' : undefined}
              >
                {group.hits.map((hit) => (
                  <ResultRow
                    key={hit.id}
                    onSelect={() =>
                      handleSelect(searchHitHref(hit.kind, hit.id, hit.projectId))
                    }
                    title={hit.title}
                    subtitle={hitSubtitle(hit)}
                  />
                ))}
              </Section>
            ))}

            {isSearching && !hasContent && (
              <p className="px-1 py-6 text-center text-[13px] text-text-muted">Søger…</p>
            )}

            {!isSearching && pages.length === 0 && !hasContent && (
              <p className="px-1 py-6 text-center text-[13px] text-text-muted">
                {debounced.length < MINIMUM_QUERY_LENGTH
                  ? 'Skriv mindst to tegn for at søge.'
                  : `Ingen resultater for “${debounced}”.`}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Anden linje under et resultat: nummer og projekt.
 *
 * Projektnavnet udelades, når det allerede er overskriften — ellers står der
 * "Havnehuset · Havnehuset" på hvert eneste projekttræf, og gentagelsen får
 * linjen til at ligne en fejl frem for en oplysning.
 */
function hitSubtitle(hit: SearchHit): string | undefined {
  const parts = [hit.subtitle]

  if (hit.projectName && hit.projectName !== hit.title) {
    parts.push(hit.projectName)
  }

  return parts.filter(Boolean).join(' · ') || undefined
}

function Section({
  label,
  note,
  children,
}: {
  label: string
  note?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="flex items-baseline justify-between px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        {label}
        {note && <span className="font-normal normal-case tracking-normal">{note}</span>}
      </p>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  )
}

function ResultRow({
  onSelect,
  title,
  subtitle,
  children,
}: {
  onSelect: () => void
  title: string
  subtitle?: string
  children?: React.ReactNode
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left',
          'text-[13px] text-text-secondary transition-colors',
          'hover:bg-overlay-strong hover:text-text-primary',
          'focus-visible:bg-overlay-strong focus-visible:outline-2 focus-visible:outline-brand-400',
        )}
      >
        {children}
        <span className="min-w-0 flex-1">
          <span className="block truncate">{title}</span>
          {subtitle && <span className="block truncate text-[11px] text-text-muted">{subtitle}</span>}
        </span>
      </button>
    </li>
  )
}
